import React, { useState, useEffect, useContext } from "react"
import { CALENDAR_MONTHS } from "../../utils/exports/months"
import { db } from "../../utils/firebase"
import firebase from "firebase"
import moment from "moment"
import { AuthContext } from "../../utils/providers/auth"
import "./suppliers.scss"

// UI components
import SearchSelect from "../../components/ui/select/search-select"
import Checkbox from "../../components/ui/checkbox/checkbox"
import Table from "../../components/structure/table/table"
import Title from "../../components/structure/title/title"
import Booking from "../../components/suppliers/booking"
import Tile from "../../components/structure/tile/tile"
import Select from "../../components/ui/select/select"
import Button from "../../components/ui/button/button"

// Chart.js imports
import { Bar } from "react-chartjs-2"
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js"
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend)

// Build up a list of years for the select field
let yearsObj = {}
let nextYear = Number(moment().startOf("year").add(2, "years").format("YYYY"))
for (let i = nextYear; i >= 2018; i--) {
	yearsObj = {
		[i]: i,
		...yearsObj,
	}
}

// User IDs allowed to view charts and YoY controls
const ALLOWED_CHART_USER_IDS = [
	"CGOHCdUwFAOe13OSTY2D9qdO2LX2",
	"iUlTXjfx1OZEPBhPosh6rJt3CKe2",
	"7GFYd2EvA9OiZxcufgbz5CRcssu1",
	"FbeBRh8bIGb9L6aRLLJSttvutQ33",
	"Z6EM3kFyJ8QiJMzMsYm6TwrWaUH3",
	"Y8eKcR9e2mgrSX4Bqj7yIGcacgm2",
]

// Functional component to return the dashboard for viewing travellers by various dates
export default function Suppliers() {
	const { user } = useContext(AuthContext)
	const [loading, setLoading] = useState(false)
	const [bookings, setBookings] = useState([])
	const [suppliers, setSuppliers] = useState({})

	const [supplier, setSupplier] = useState({})
	const [month, setMonth] = useState("")
	const [year, setYear] = useState("")
	const [type, setType] = useState("")

	const [totalGolfers, setTotalGolfers] = useState(0)
	const [totalNonGolfers, setTotalNonGolfers] = useState(0)
	const [totalInvoiced, setTotalInvoiced] = useState(0)
	const [uniqueBookingCount, setUniqueBookingCount] = useState(0)

	// Year-over-Year comparison
	const [showYoYComparison, setShowYoYComparison] = useState(false)
	const [historicalData, setHistoricalData] = useState([])
	const [loadingCharts, setLoadingCharts] = useState(false)

	// Check if current user is allowed to view charts
	const canViewCharts = ALLOWED_CHART_USER_IDS.includes(user?.uid)

	// On component load, load in the various suppliers
	useEffect(() => {
		fetchSuppliers()
	}, [])

	// When any of the options are updated
	useEffect(() => {
		// Clear the bookings and data
		setBookings([])
		setTotalInvoiced(0)
		setTotalGolfers(0)
		setTotalNonGolfers(0)
		setUniqueBookingCount(0)
		setHistoricalData([])
	}, [supplier, month, year, type])

	// Fetch all suppliers registered on the system to be queried
	const fetchSuppliers = async () => {
		const suppliersObj = {}

		// Fetch all supplier types in parallel
		const [groupDocs, hotelDocs, courseDocs, transferDocs] = await Promise.all([
			db.collection("partner_groups").orderBy("name").get(),
			db.collection("hotels").orderBy("name").get(),
			db.collection("courses").orderBy("name").get(),
			db.collection("transfers").orderBy("name").get(),
		])

		// Process groups
		groupDocs.forEach((doc) => {
			suppliersObj[`GROUP_${doc.id}`] = `[GROUP] ${doc.data().name}`
		})

		// Process hotels
		hotelDocs.forEach((doc) => {
			suppliersObj[`HOTEL_${doc.id}`] = doc.data().name
		})

		// Process courses
		courseDocs.forEach((doc) => {
			suppliersObj[`COURSE_${doc.id}`] = doc.data().name
		})

		// Process transfers
		transferDocs.forEach((doc) => {
			suppliersObj[`TRANSFER_${doc.id}`] = doc.data().name
		})

		// Set into state
		setSuppliers(suppliersObj)
	}

	// Calculate supplier costs for a specific booking
	const calculateSupplierCosts = async (bookingID, supplierType, supplierID, booking) => {
		let cost = 0

		if (supplierType === "HOTEL") {
			// Query all hotels for this booking that match the supplier
			await db
				.collection(`bookings/${bookingID}/hotels`)
				.where("resortID", "==", supplierID)
				.get()
				.then((hotelDocs) => {
					hotelDocs.forEach((hotelDoc) => {
						const { total_cost } = hotelDoc.data()
						cost += total_cost || 0
					})
				})
		} else if (supplierType === "COURSE") {
			// Query all rounds for this booking that match the supplier
			await db
				.collection(`bookings/${bookingID}/rounds`)
				.where("courseID", "==", supplierID)
				.get()
				.then((roundDocs) => {
					roundDocs.forEach((roundDoc) => {
						const { cost_per_golfer } = roundDoc.data()
						cost += (booking.golfers || 0) * (cost_per_golfer || 0)
					})
				})
		} else if (supplierType === "TRANSFER") {
			// For transfers, use the margins cost directly
			cost = booking.margins?.transfers_cost || 0
		}

		return cost
	}

	// Fetch the supplier stats for the given supplier
	const fetchSupplierStats = async () => {
		// Clear the state
		setBookings([])

		// Show a loading spinner
		setLoading(true)

		// Get the timestamps in milliseconds for the chosen dates
		let fetchStart, fetchEnd
		if (month) {
			fetchStart = moment(`${month}-${year}`, "MMMM-YYYY").startOf("month").valueOf()
			fetchEnd = moment(`${month}-${year}`, "MMMM-YYYY").endOf("month").valueOf()
		} else {
			fetchStart = moment(`${year}`, "YYYY").startOf("year").valueOf()
			fetchEnd = moment(`${year}`, "YYYY").endOf("year").valueOf()
		}

		// Build some timestamps for the query
		const startTimestamp = firebase.firestore.Timestamp.fromMillis(fetchStart)
		const endTimestamp = firebase.firestore.Timestamp.fromMillis(fetchEnd)

		// Initially we'll query on the check_in dates
		let searchBy = "check_in"

		// Set search direction based on input
		if (type === "travel_date") {
			searchBy = "check_in"
		} else if (type === "booked_date") {
			searchBy = "booked"
		}

		// Split the supplier ID
		const supplierParts = supplier.option.split("_")

		// Get the type and ID of the supplier
		const supplierType = supplierParts[0]
		const supplierID = supplierParts[1]

		// Process the relevant bookings query for the supplier type
		if (supplierType === "GROUP") {
			await fetchGroupDocuments(searchBy, startTimestamp, endTimestamp, supplierID)
		} else if (supplierType === "HOTEL") {
			await fetchHotelDocuments(searchBy, startTimestamp, endTimestamp, supplierID)
		} else if (supplierType === "COURSE") {
			await fetchCourseDocuments(searchBy, startTimestamp, endTimestamp, supplierID)
		} else if (supplierType === "TRANSFER") {
			await fetchTransferDocuments(searchBy, startTimestamp, endTimestamp, supplierID)
		}

		// If YoY comparison is enabled, fetch historical data
		if (showYoYComparison && canViewCharts) {
			setLoadingCharts(true)
			await fetchHistoricalData(searchBy, startTimestamp, endTimestamp, supplierType, supplierID)
			setLoadingCharts(false)
		}
	}

	// Fetch all the documents associated with a group of suppliers
	const fetchGroupDocuments = async (searchBy, startTimestamp, endTimestamp, groupID) => {
		// First we need to get a list of suppliers to query from the group
		const groupSuppliers = await db
			.doc(`partner_groups/${groupID}`)
			.get()
			.then((groupDoc) => {
				return Object.keys(groupDoc.data().partners)
			})

		// Setup an array to write all the bookings to
		let bookingsArr = []

		// Then we need to loop through the suppliers and query based on type
		await Promise.all(
			groupSuppliers.map(async (groupSupplier) => {
				// Split the supplier ID
				const supplierParts = groupSupplier.split("_")

				// Pull both the type and document ID for them
				const supplierType = supplierParts[0]
				const supplierID = supplierParts[1]

				// Then query based on the type
				if (supplierType === "HOTEL") {
					await db
						.collection("bookings")
						.where(`${searchBy}`, ">=", startTimestamp)
						.where(`${searchBy}`, "<=", endTimestamp)
						.where("suppliers_hotels", "array-contains", supplierID)
						.where("removed", "==", false)
						.orderBy(`${searchBy}`)
						.get()
						.then((bookingDocs) => {
							// Store a local copy of bookings for this supplier
							let localBookingsArr = []

							// Loop through what was found and write them to the array
							bookingDocs.forEach((bookingDoc) => {
								localBookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data(), stats_supplier: groupSupplier })
							})

							// Then push this array to our local one further up
							bookingsArr.push(...localBookingsArr)
						})
				}

				// If we are looking at courses
				if (supplierType === "COURSE") {
					await db
						.collection("bookings")
						.where(`${searchBy}`, ">=", startTimestamp)
						.where(`${searchBy}`, "<=", endTimestamp)
						.where("suppliers_rounds", "array-contains", supplierID)
						.where("removed", "==", false)
						.orderBy(`${searchBy}`)
						.get()
						.then((bookingDocs) => {
							// Store a local copy of bookings for this supplier
							let localBookingsArr = []

							// Loop through what was found and write them to the array
							bookingDocs.forEach((bookingDoc) => {
								localBookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data(), stats_supplier: groupSupplier })
							})

							// Then push this array to our local one further up
							bookingsArr.push(...localBookingsArr)
						})
				}
			})
		)

		// Process all bookings with batched data
		await processBookingsData(bookingsArr)

		// Hide the loading spinners
		setLoading(false)
	}

	// Fetch the suppliers data if they're a hotel
	const fetchHotelDocuments = async (searchBy, startTimestamp, endTimestamp, supplierID) => {
		// Pull all the bookings by their travel date
		await db
			.collection("bookings")
			.where(`${searchBy}`, ">=", startTimestamp)
			.where(`${searchBy}`, "<=", endTimestamp)
			.where("suppliers_hotels", "array-contains", supplierID)
			.where("removed", "==", false)
			.orderBy(`${searchBy}`)
			.get()
			.then(async (bookingDocs) => {
				// Create an empty array for storing the bookings
				let bookingsArr = []

				// Loop through the bookings that have been found that match
				bookingDocs.forEach((bookingDoc) => {
					// Push the booking data into the array
					bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data() })
				})

				// Process all bookings with batched data
				await processBookingsData(bookingsArr)
			})

		// Reset the state
		setLoading(false)
	}

	// Fetch the suppliers data if they're a course
	const fetchCourseDocuments = async (searchBy, startTimestamp, endTimestamp, supplierID) => {
		// Pull all the bookings by their travel date
		await db
			.collection("bookings")
			.where(`${searchBy}`, ">=", startTimestamp)
			.where(`${searchBy}`, "<=", endTimestamp)
			.where("suppliers_rounds", "array-contains", supplierID)
			.where("removed", "==", false)
			.orderBy(`${searchBy}`)
			.get()
			.then(async (bookingDocs) => {
				// Create an empty array for storing the bookings
				let bookingsArr = []

				// Loop through the bookings that have been found that match
				bookingDocs.forEach((bookingDoc) => {
					// Push the booking data into the array
					bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data() })
				})

				// Process all bookings with batched data
				await processBookingsData(bookingsArr)
			})

		// Reset the state
		setLoading(false)
	}

	// Fetch the suppliers data if they're a transfer
	const fetchTransferDocuments = async (searchBy, startTimestamp, endTimestamp, supplierID) => {
		// Pull all the bookings by their travel date
		await db
			.collection("bookings")
			.where(`${searchBy}`, ">=", startTimestamp)
			.where(`${searchBy}`, "<=", endTimestamp)
			.where("transfer", "==", supplierID)
			.where("removed", "==", false)
			.orderBy(`${searchBy}`)
			.get()
			.then(async (bookingDocs) => {
				// Create an empty array for storing the bookings
				let bookingsArr = []

				// Loop through the bookings that have been found that match
				bookingDocs.forEach((bookingDoc) => {
					// Push the booking data into the array
					bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data() })
				})

				// Process all bookings with batched data
				await processBookingsData(bookingsArr)
			})

		// Reset the state
		setLoading(false)
	}

	// Process bookings data - batch fetch clients, agents, suppliers and calculate costs
	const processBookingsData = async (bookingsArr) => {
		// Extract unique IDs for batch fetching
		const clientIDs = [...new Set(bookingsArr.map((b) => b.client).filter(Boolean))]
		const agentIDs = [...new Set(bookingsArr.map((b) => b.agent).filter(Boolean))]

		// Extract unique supplier IDs by type
		const suppliersByType = { HOTEL: new Set(), COURSE: new Set(), TRANSFER: new Set() }
		bookingsArr.forEach((booking) => {
			const supplierKey = booking.stats_supplier || supplier.option
			const [supplierType, supplierID] = supplierKey.split("_")
			if (suppliersByType[supplierType]) {
				suppliersByType[supplierType].add(supplierID)
			}
		})

		// Batch fetch all clients, agents, and suppliers in parallel
		const [clientsObj, agentsObj, suppliersObj] = await Promise.all([
			// Fetch clients
			Promise.all(
				clientIDs.map(async (clientID) => {
					const clientDoc = await db.doc(`clients/${clientID}`).get()
					return clientDoc.exists ? [clientID, clientDoc.data()] : null
				})
			).then((results) => Object.fromEntries(results.filter(Boolean))),

			// Fetch agents
			Promise.all(
				agentIDs.map(async (agentID) => {
					const agentDoc = await db.doc(`users/${agentID}`).get()
					return agentDoc.exists ? [agentID, agentDoc.data()] : null
				})
			).then((results) => Object.fromEntries(results.filter(Boolean))),

			// Fetch suppliers
			(async () => {
				const suppliers = {}
				await Promise.all([
					...Array.from(suppliersByType.HOTEL).map(async (id) => {
						const doc = await db.doc(`hotels/${id}`).get()
						if (doc.exists) suppliers[`HOTEL_${id}`] = doc.data()
					}),
					...Array.from(suppliersByType.COURSE).map(async (id) => {
						const doc = await db.doc(`courses/${id}`).get()
						if (doc.exists) suppliers[`COURSE_${id}`] = doc.data()
					}),
					...Array.from(suppliersByType.TRANSFER).map(async (id) => {
						const doc = await db.doc(`transfers/${id}`).get()
						if (doc.exists) suppliers[`TRANSFER_${id}`] = doc.data()
					}),
				])
				return suppliers
			})(),
		])

		// Calculate costs and build final data structure
		const bookingsWithData = await Promise.all(
			bookingsArr.map(async (booking) => {
				const supplierKey = booking.stats_supplier || supplier.option
				const [supplierType, supplierID] = supplierKey.split("_")

				// Calculate supplier cost for this booking
				const supplierCost = await calculateSupplierCosts(booking.id, supplierType, supplierID, booking)

				return {
					...booking,
					clientData: clientsObj[booking.client] || {},
					agentData: agentsObj[booking.agent] || {},
					supplierDetails: suppliersObj[supplierKey] || {},
					supplierCost,
					supplierType,
				}
			})
		)

		// Calculate totals - need to deduplicate bookings for golfers/non-golfers count
		// but sum ALL supplier costs (each row is a different service)
		const uniqueBookings = new Set()
		let totalGolfersCount = 0
		let totalNonGolfersCount = 0
		let totalSupplierCost = 0

		bookingsWithData.forEach((booking) => {
			// Only count golfers/non-golfers once per unique booking
			if (!uniqueBookings.has(booking.id)) {
				uniqueBookings.add(booking.id)
				totalGolfersCount += booking.golfers || 0
				totalNonGolfersCount += booking.non_golfers || 0
			}

			// Always sum supplier costs (each row is a different line item)
			totalSupplierCost += booking.supplierCost || 0
		})

		// Update state
		setBookings(bookingsWithData)
		setUniqueBookingCount(uniqueBookings.size)
		setTotalGolfers(totalGolfersCount)
		setTotalNonGolfers(totalNonGolfersCount)
		setTotalInvoiced(totalSupplierCost.toLocaleString())
	}

	// Fetch and calculate historical data
	const fetchHistoricalData = async (searchBy, currentStartTimestamp, currentEndTimestamp, supplierType, supplierID) => {
		const currentStart = moment(currentStartTimestamp.toMillis())
		const currentEnd = moment(currentEndTimestamp.toMillis())

		// For GROUP type, fetch group suppliers once
		let groupSuppliers = null
		if (supplierType === "GROUP") {
			groupSuppliers = await db
				.doc(`partner_groups/${supplierID}`)
				.get()
				.then((groupDoc) => Object.keys(groupDoc.data().partners))
		}

		const historicalYears = []

		// Fetch data for past years (5 years back)
		for (let yearsBack = 1; yearsBack <= 5; yearsBack++) {
			const yearStart = currentStart.clone().subtract(yearsBack, "year")
			const yearEnd = currentEnd.clone().subtract(yearsBack, "year")

			const startTimestamp = firebase.firestore.Timestamp.fromMillis(yearStart.valueOf())
			const endTimestamp = firebase.firestore.Timestamp.fromMillis(yearEnd.valueOf())

			let bookingsArr = []

			// Fetch bookings based on supplier type
			if (supplierType === "GROUP") {
				await Promise.all(
					groupSuppliers.map(async (groupSupplier) => {
						const supplierParts = groupSupplier.split("_")
						const sType = supplierParts[0]
						const sID = supplierParts[1]

						if (sType === "HOTEL") {
							await db
								.collection("bookings")
								.where(`${searchBy}`, ">=", startTimestamp)
								.where(`${searchBy}`, "<=", endTimestamp)
								.where("suppliers_hotels", "array-contains", sID)
								.where("removed", "==", false)
								.orderBy(`${searchBy}`)
								.get()
								.then((bookingDocs) => {
									bookingDocs.forEach((bookingDoc) => {
										bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data(), stats_supplier: groupSupplier })
									})
								})
						} else if (sType === "COURSE") {
							await db
								.collection("bookings")
								.where(`${searchBy}`, ">=", startTimestamp)
								.where(`${searchBy}`, "<=", endTimestamp)
								.where("suppliers_rounds", "array-contains", sID)
								.where("removed", "==", false)
								.orderBy(`${searchBy}`)
								.get()
								.then((bookingDocs) => {
									bookingDocs.forEach((bookingDoc) => {
										bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data(), stats_supplier: groupSupplier })
									})
								})
						}
					})
				)
			} else if (supplierType === "HOTEL") {
				await db
					.collection("bookings")
					.where(`${searchBy}`, ">=", startTimestamp)
					.where(`${searchBy}`, "<=", endTimestamp)
					.where("suppliers_hotels", "array-contains", supplierID)
					.where("removed", "==", false)
					.orderBy(`${searchBy}`)
					.get()
					.then((bookingDocs) => {
						bookingDocs.forEach((bookingDoc) => {
							bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data() })
						})
					})
			} else if (supplierType === "COURSE") {
				await db
					.collection("bookings")
					.where(`${searchBy}`, ">=", startTimestamp)
					.where(`${searchBy}`, "<=", endTimestamp)
					.where("suppliers_rounds", "array-contains", supplierID)
					.where("removed", "==", false)
					.orderBy(`${searchBy}`)
					.get()
					.then((bookingDocs) => {
						bookingDocs.forEach((bookingDoc) => {
							bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data() })
						})
					})
			} else if (supplierType === "TRANSFER") {
				await db
					.collection("bookings")
					.where(`${searchBy}`, ">=", startTimestamp)
					.where(`${searchBy}`, "<=", endTimestamp)
					.where("transfer", "==", supplierID)
					.where("removed", "==", false)
					.orderBy(`${searchBy}`)
					.get()
					.then((bookingDocs) => {
						bookingDocs.forEach((bookingDoc) => {
							bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data() })
						})
					})
			}

			// Calculate costs for each booking
			const bookingsWithCosts = await Promise.all(
				bookingsArr.map(async (booking) => {
					const sKey = booking.stats_supplier || `${supplierType}_${supplierID}`
					const sParts = sKey.split("_")
					const sType = sParts[0]
					const sID = sParts[1]

					const cost = await calculateSupplierCosts(booking.id, sType, sID, booking)
					return { ...booking, supplierCost: cost }
				})
			)

			// Calculate year totals
			const uniqueBookings = new Set()
			let yearTotalGolfers = 0
			let yearTotalNonGolfers = 0
			let yearTotalCost = 0

			bookingsWithCosts.forEach((booking) => {
				if (!uniqueBookings.has(booking.id)) {
					uniqueBookings.add(booking.id)
					yearTotalGolfers += booking.golfers || 0
					yearTotalNonGolfers += booking.non_golfers || 0
				}
				yearTotalCost += booking.supplierCost || 0
			})

			historicalYears.push({
				year: parseInt(year) - yearsBack,
				bookingCount: uniqueBookings.size,
				golfers: yearTotalGolfers,
				nonGolfers: yearTotalNonGolfers,
				supplierCost: yearTotalCost,
			})
		}

		// For travel_date searches, also fetch the next 2 years
		if (type === "travel_date") {
			for (let yearsForward = 1; yearsForward <= 2; yearsForward++) {
				const yearStart = currentStart.clone().add(yearsForward, "year")
				const yearEnd = currentEnd.clone().add(yearsForward, "year")

				const startTimestamp = firebase.firestore.Timestamp.fromMillis(yearStart.valueOf())
				const endTimestamp = firebase.firestore.Timestamp.fromMillis(yearEnd.valueOf())

				let bookingsArr = []

				// Fetch bookings based on supplier type
				if (supplierType === "GROUP") {
					await Promise.all(
						groupSuppliers.map(async (groupSupplier) => {
							const supplierParts = groupSupplier.split("_")
							const sType = supplierParts[0]
							const sID = supplierParts[1]

							if (sType === "HOTEL") {
								await db
									.collection("bookings")
									.where(`${searchBy}`, ">=", startTimestamp)
									.where(`${searchBy}`, "<=", endTimestamp)
									.where("suppliers_hotels", "array-contains", sID)
									.where("removed", "==", false)
									.orderBy(`${searchBy}`)
									.get()
									.then((bookingDocs) => {
										bookingDocs.forEach((bookingDoc) => {
											bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data(), stats_supplier: groupSupplier })
										})
									})
							} else if (sType === "COURSE") {
								await db
									.collection("bookings")
									.where(`${searchBy}`, ">=", startTimestamp)
									.where(`${searchBy}`, "<=", endTimestamp)
									.where("suppliers_rounds", "array-contains", sID)
									.where("removed", "==", false)
									.orderBy(`${searchBy}`)
									.get()
									.then((bookingDocs) => {
										bookingDocs.forEach((bookingDoc) => {
											bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data(), stats_supplier: groupSupplier })
										})
									})
							}
						})
					)
				} else if (supplierType === "HOTEL") {
					await db
						.collection("bookings")
						.where(`${searchBy}`, ">=", startTimestamp)
						.where(`${searchBy}`, "<=", endTimestamp)
						.where("suppliers_hotels", "array-contains", supplierID)
						.where("removed", "==", false)
						.orderBy(`${searchBy}`)
						.get()
						.then((bookingDocs) => {
							bookingDocs.forEach((bookingDoc) => {
								bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data() })
							})
						})
				} else if (supplierType === "COURSE") {
					await db
						.collection("bookings")
						.where(`${searchBy}`, ">=", startTimestamp)
						.where(`${searchBy}`, "<=", endTimestamp)
						.where("suppliers_rounds", "array-contains", supplierID)
						.where("removed", "==", false)
						.orderBy(`${searchBy}`)
						.get()
						.then((bookingDocs) => {
							bookingDocs.forEach((bookingDoc) => {
								bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data() })
							})
						})
				} else if (supplierType === "TRANSFER") {
					await db
						.collection("bookings")
						.where(`${searchBy}`, ">=", startTimestamp)
						.where(`${searchBy}`, "<=", endTimestamp)
						.where("transfer", "==", supplierID)
						.where("removed", "==", false)
						.orderBy(`${searchBy}`)
						.get()
						.then((bookingDocs) => {
							bookingDocs.forEach((bookingDoc) => {
								bookingsArr.push({ id: bookingDoc.id, ...bookingDoc.data() })
							})
						})
				}

				// Calculate costs for each booking
				const bookingsWithCosts = await Promise.all(
					bookingsArr.map(async (booking) => {
						const sKey = booking.stats_supplier || `${supplierType}_${supplierID}`
						const sParts = sKey.split("_")
						const sType = sParts[0]
						const sID = sParts[1]

						const cost = await calculateSupplierCosts(booking.id, sType, sID, booking)
						return { ...booking, supplierCost: cost }
					})
				)

				// Calculate year totals
				const uniqueBookings = new Set()
				let yearTotalGolfers = 0
				let yearTotalNonGolfers = 0
				let yearTotalCost = 0

				bookingsWithCosts.forEach((booking) => {
					if (!uniqueBookings.has(booking.id)) {
						uniqueBookings.add(booking.id)
						yearTotalGolfers += booking.golfers || 0
						yearTotalNonGolfers += booking.non_golfers || 0
					}
					yearTotalCost += booking.supplierCost || 0
				})

				historicalYears.push({
					year: parseInt(year) + yearsForward,
					bookingCount: uniqueBookings.size,
					golfers: yearTotalGolfers,
					nonGolfers: yearTotalNonGolfers,
					supplierCost: yearTotalCost,
				})
			}
		}

		// Sort historical years in chronological order
		historicalYears.sort((a, b) => a.year - b.year)

		setHistoricalData(historicalYears)
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex has-select-field">
				<h1>Supplier stats</h1>
			</Title>

			<div className="page-filters extra">
				<SearchSelect
					label="Supplier:"
					placeholder="Supplier:"
					showClear={true}
					activeOnHover={true}
					options={suppliers}
					onSelect={(option) => setSupplier(option)}
					hideArrow={supplier.value?.length > 0}
					clearInput={() => setSupplier({})}
				/>

				<Select
					label="Month:"
					placeholder="Month:"
					value={month}
					activeOnHover={true}
					onSelect={(option) => setMonth(option.option)}
					options={CALENDAR_MONTHS}
				/>

				<Select
					label="Year:"
					placeholder="Year:"
					value={year}
					activeOnHover={true}
					onSelect={(option) => setYear(option.option)}
					options={yearsObj}
				/>

				<Select
					label="Search by:"
					placeholder="Search by:"
					value={type}
					selected={type}
					activeOnHover={true}
					onSelect={(option) => setType(option.option)}
					options={{
						travel_date: "Travel date",
						booked_date: "Booked date",
					}}
				/>

				<div className="yoy-controls">
					<Button
						filters={true}
						disabled={!supplier?.option || !year || !type}
						label="Fetch"
						loadingText="Fetching..."
						loading={loading}
						onClick={() => fetchSupplierStats()}
					/>
					{canViewCharts && (
						<Checkbox
							label="Show YoY comparison"
							checked={showYoYComparison}
							onClick={() => setShowYoYComparison(!showYoYComparison)}
						/>
					)}
				</div>
			</div>

			{bookings.length > 0 && (
				<div className="supplier-summary">
					<div className="summary-card">
						<span>Total Bookings</span>
						<strong>{uniqueBookingCount}</strong>
					</div>
					<div className="summary-card">
						<span>Total Golfers</span>
						<strong>{totalGolfers}</strong>
					</div>
					<div className="summary-card">
						<span>Total Non-Golfers</span>
						<strong>{totalNonGolfers}</strong>
					</div>
					<div className="summary-card">
						<span>Total Supplier Cost</span>
						<strong>£{totalInvoiced || 0}</strong>
					</div>
				</div>
			)}

			{showYoYComparison && bookings.length > 0 && canViewCharts && (
				<div className="yoy-chart-container">
					<h3>Historical Comparison - Last {type === "travel_date" ? "7" : "5"} Years</h3>

					{loadingCharts && (
						<div className="charts-loading">
							<div className="spinner"></div>
							<p>Loading historical data...</p>
						</div>
					)}

					{!loadingCharts && historicalData.length > 0 && (
						<div className="charts-grid">
							{/* Bookings & Travellers Chart */}
							<div className="yoy-chart">
								<h4>Bookings & Travellers</h4>
								<Bar
									data={{
										labels: (() => {
											const allYears = [...historicalData.map((d) => d.year)]
											const currentYearInt = parseInt(year)
											if (!allYears.includes(currentYearInt)) {
												allYears.push(currentYearInt)
												allYears.sort((a, b) => a - b)
											}
											return allYears
										})(),
										datasets: [
											{
												label: "Bookings",
												data: (() => {
													const allYears = [...historicalData.map((d) => d.year)]
													const currentYearInt = parseInt(year)
													const bookingCounts = [...historicalData.map((d) => d.bookingCount)]
													if (!allYears.includes(currentYearInt)) {
														allYears.push(currentYearInt)
														bookingCounts.push(uniqueBookingCount)
														// Sort both arrays together
														const combined = allYears.map((year, i) => ({ year, count: bookingCounts[i] }))
														combined.sort((a, b) => a.year - b.year)
														return combined.map((item) => item.count)
													}
													return bookingCounts
												})(),
												backgroundColor: "#e41f13",
												borderColor: "#e41f13",
												borderWidth: 2,
											},
											{
												label: "Travellers",
												data: (() => {
													const allYears = [...historicalData.map((d) => d.year)]
													const currentYearInt = parseInt(year)
													const travellerCounts = [...historicalData.map((d) => d.golfers + d.nonGolfers)]
													if (!allYears.includes(currentYearInt)) {
														allYears.push(currentYearInt)
														travellerCounts.push(totalGolfers + totalNonGolfers)
														// Sort both arrays together
														const combined = allYears.map((year, i) => ({ year, count: travellerCounts[i] }))
														combined.sort((a, b) => a.year - b.year)
														return combined.map((item) => item.count)
													}
													return travellerCounts
												})(),
												backgroundColor: "#2a66bf",
												borderColor: "#2a66bf",
												borderWidth: 2,
											},
										],
									}}
									options={{
										responsive: true,
										maintainAspectRatio: false,
										plugins: {
											legend: {
												position: "top",
												labels: {
													font: {
														size: 13,
														weight: "500",
													},
													padding: 15,
													usePointStyle: true,
												},
											},
											tooltip: {
												backgroundColor: "rgba(0, 0, 0, 0.8)",
												padding: 12,
												titleFont: {
													size: 14,
													weight: "600",
												},
												bodyFont: {
													size: 13,
												},
												callbacks: {
													label: function (context) {
														let label = context.dataset.label || ""
														if (label) {
															label += ": "
														}
														label += context.parsed.y.toLocaleString()

														// Calculate YoY change (compare with previous index, which is previous year)
														if (context.dataIndex > 0) {
															const prevYearValue = context.dataset.data[context.dataIndex - 1]
															if (prevYearValue) {
																const diff = context.parsed.y - prevYearValue
																const percentDiff = prevYearValue !== 0 ? ((diff / prevYearValue) * 100).toFixed(1) : "N/A"
																if (percentDiff !== "N/A") {
																	label += ` (${diff >= 0 ? "+" : ""}${percentDiff}% YoY)`
																}
															}
														}
														return label
													},
												},
											},
										},
										scales: {
											x: {
												grid: {
													display: false,
												},
												ticks: {
													font: {
														size: 12,
														weight: "500",
													},
												},
											},
											y: {
												beginAtZero: true,
												grid: {
													color: "rgba(0, 0, 0, 0.05)",
												},
												ticks: {
													font: {
														size: 12,
													},
													callback: function (value) {
														return value.toLocaleString()
													},
												},
											},
										},
									}}
								/>
							</div>

							{/* Supplier Cost Chart */}
							<div className="yoy-chart">
								<h4>Supplier Cost</h4>
								<Bar
									data={{
										labels: (() => {
											const allYears = [...historicalData.map((d) => d.year)]
											const currentYearInt = parseInt(year)
											if (!allYears.includes(currentYearInt)) {
												allYears.push(currentYearInt)
												allYears.sort((a, b) => a - b)
											}
											return allYears
										})(),
										datasets: [
											{
												label: "Supplier Cost (£)",
												data: (() => {
													const allYears = [...historicalData.map((d) => d.year)]
													const currentYearInt = parseInt(year)
													const supplierCosts = [...historicalData.map((d) => d.supplierCost)]
													if (!allYears.includes(currentYearInt)) {
														allYears.push(currentYearInt)
														supplierCosts.push(parseFloat(totalInvoiced.replace(/,/g, "") || 0))
														// Sort both arrays together
														const combined = allYears.map((year, i) => ({ year, cost: supplierCosts[i] }))
														combined.sort((a, b) => a.year - b.year)
														return combined.map((item) => item.cost)
													}
													return supplierCosts
												})(),
												backgroundColor: "#b32abf",
												borderColor: "#b32abf",
												borderWidth: 2,
											},
										],
									}}
									options={{
										responsive: true,
										maintainAspectRatio: false,
										plugins: {
											legend: {
												position: "top",
												labels: {
													font: {
														size: 13,
														weight: "500",
													},
													padding: 15,
													usePointStyle: true,
												},
											},
											tooltip: {
												backgroundColor: "rgba(0, 0, 0, 0.8)",
												padding: 12,
												titleFont: {
													size: 14,
													weight: "600",
												},
												bodyFont: {
													size: 13,
												},
												callbacks: {
													label: function (context) {
														let label = "Supplier Cost: £" + context.parsed.y.toLocaleString()

														// Calculate YoY change (compare with previous index, which is previous year)
														if (context.dataIndex > 0) {
															const prevYearValue = context.dataset.data[context.dataIndex - 1]
															if (prevYearValue) {
																const diff = context.parsed.y - prevYearValue
																const percentDiff = prevYearValue !== 0 ? ((diff / prevYearValue) * 100).toFixed(1) : "N/A"
																if (percentDiff !== "N/A") {
																	label += ` (${diff >= 0 ? "+" : ""}${percentDiff}% YoY)`
																}
															}
														}
														return label
													},
												},
											},
										},
										scales: {
											x: {
												grid: {
													display: false,
												},
												ticks: {
													font: {
														size: 12,
														weight: "500",
													},
												},
											},
											y: {
												beginAtZero: true,
												grid: {
													color: "rgba(0, 0, 0, 0.05)",
												},
												ticks: {
													font: {
														size: 12,
													},
													callback: function (value) {
														return "£" + value.toLocaleString()
													},
												},
											},
										},
									}}
								/>
							</div>
						</div>
					)}
				</div>
			)}

			<Table
				className="travellers-table"
				headings={["Booking ref.", "Agent", type === "booked_date" ? "Booked" : "Travelling", "Lead pax", "Golfers", "Non golfers", "Confirmed", "Supplier", "Supplier cost", "Paid", ""]}
				noResults={Object.entries(bookings).length === 0 || !supplier?.option || !year || !type}
				noResultsMessage={"No results matching those filters"}>
				{bookings.map((booking, index) => (
					<Booking
						key={`${booking?.id}_${booking?.stats_supplier || supplier?.option}_${index}`}
						id={booking?.id}
						details={booking}
						sorting={type}
						client={booking.clientData}
						agent={booking.agentData}
						supplierDetails={booking.supplierDetails}
						supplierCost={booking.supplierCost}
						supplierType={booking.supplierType}
					/>
				))}
			</Table>
		</Tile>
	)
}
